<template>
  <FormContainer>
    <template #title>Diplôme ou titre visé par l'apprenti(e)</template>
    <template #content>
      <div class="form__bloc">
        <WrapperDropdown
          id="field_246"
          v-model="diplomeOuTitreViseModel"
          label="Diplôme ou titre visé par l’apprenti"
          :required="meta.formation?.diplome?.$status === PropertyStatus.Required"
          :alertLabel="meta.formation?.diplome?.$messageErreurDirty!"
          class="col-18 col-md-18 col-sm-36"
          :items="lov.Diplome"
          :readonly
          key-id="code"
          key-label="intitule"
        />
      </div>
      <div class="form__bloc">
        <WrapperToggleSwitch
          id="field_13"
          v-model="typeQualificationModel"
          label="Recherche par code diplôme ou RNCP"
          :items="typeQualificationItems"
          :required="meta.formation?.typeQualification?.$status === PropertyStatus.Required"
          :alertLabel="meta.formation?.typeQualification?.$messageErreurDirty!"
          :readonly
          help="Le code diplôme peut être déduit du code RNCP et à l'inverse, vous pouvez renseigner un code diplôme pour déduire le code RNCP correspondant"
          class="col-10 col-md-14 col-sm-36"
        />
        <WrapperAutocomplete
          id="field_176"
          key-id="code"
          key-label="intitule"
          :get-input-text="(item) => item.code"
          :get-item-label="getLabel"
          v-model="codeRNCP"
          :search="(search) => searchRNCP({ search, codeDiplome: codeDiplome?.code })"
          label="Code RNCP"
          hint="Code ou intitulé"
          class="col-13 col-md-18 col-sm-36"
          :search-min-length="typeQualificationModel === 'CodeDiplome' ? 0 : 3"
          placeholder="Ex: boulangerie ou 39257"
          :readonly
          strict
          :required="meta.formation?.rncp?.code?.$status === PropertyStatus.Required"
          :alertLabel="meta.formation?.rncp?.code?.$messageErreurDirty!"
          :disabled="codeRncpDisabled"
        />
        <WrapperAutocomplete
          id="field_177"
          key-id="code"
          key-label="intitule"
          :get-input-text="(item) => item.code"
          :get-item-label="getLabel"
          v-model="codeDiplome"
          :search="(search) => searchCodeDiplome({ search, codeRNCP: codeRNCP?.code })"
          label="Code Diplôme"
          hint="Code ou intitulé"
          class="col-13 col-md-18 col-sm-36"
          :search-min-length="typeQualificationModel === 'Rncp' ? 0 : 3"
          placeholder="EX : boulangerie ou 45022108"
          :readonly
          strict
          :required="meta.formation?.codeDiplome?.code?.$status === PropertyStatus.Required"
          :alertLabel="meta.formation?.codeDiplome?.code?.$messageErreurDirty!"
          :disabled="codeDiplomeDisabled"
        />
      </div>
      <WrapperInput
        id="field_175"
        :model-value="intitulé"
        label="Intitulé précis"
        :alertLabel="meta.formation?.rncp?.intitule?.$messageErreurDirty!"
        disabled
        class="col-36"
      />
      <WarningRncp
        :rncp="codeRNCP"
        :date-conclusion
        :isAvenant
        :get-rncp
        :readonly
        class="col-36"
      />
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import { FormContainer, WarningRncp } from '@/features/interface/demande/shared'
  import { useDemandeCAPPStore } from '@/features/interface/demande/capp/useDemandeCAPPStore'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { CAPP_ACTIONS, type CappActions } from '@/features/interface/demande/capp/DemandeCAPP.type'
  import type { LovItemDTO, RncpDto, CodeDiplomeDTO } from '@/features/api/extranet/dto/Lov'
  import { typeQualificationItems } from '@/features/interface/demande/capp/dropdown-items'
  import { TypeQualification } from '@/features/domaine/types/opco/Demande/CAPP'
  import { typeContratAvenantRncp } from '@/features/domaine/regles-akto/CAPP/constantes'

  const { searchRNCP, searchCodeDiplome, getRncp } = inject<CappActions>(CAPP_ACTIONS)!

  const store = useDemandeCAPPStore()
  const { setupFieldWithOptions, setupFieldWithKey } = store
  const { meta, model, readonly, lov } = storeToRefs(store)

  const diplomeOuTitreViseModel = setupFieldWithKey<string>('formation.diplome', false)

  const dateConclusion = computed(() => model.value?.contrat?.dateConclusion)
  const isAvenant = computed(() => typeContratAvenantRncp.includes(model.value?.contrat?.typeContratAvenant ?? ''))

  const codeRNCP = setupFieldWithOptions<LovItemDTO<string> | null>({
    getValue: () => model.value?.formation?.rncp ?? null,
    setPatch: (patch, value) => {
      patch.formation!.rncp = !value ? null : { code: value.code, intitule: value.intitule }
      if (patch.formation?.typeQualification === 'Rncp') {
        const rncpInfo = value as RncpDto | null
        patch.formation!.codeDiplome =
          rncpInfo?.codesDiplome?.length === 1
            ? { code: rncpInfo.codesDiplome[0].code, intitule: rncpInfo.codesDiplome[0].intitule }
            : null
      }
    },
  })

  const codeDiplome = setupFieldWithOptions<LovItemDTO<string> | null>({
    getValue: () => model.value?.formation?.codeDiplome ?? null,
    setPatch: (patch, value) => {
      patch.formation!.codeDiplome = !value ? null : { code: value.code, intitule: value.intitule }

      if (patch.formation?.typeQualification === 'CodeDiplome') {
        const codeDiplomeInfo = value as CodeDiplomeDTO | null
        patch.formation!.rncp =
          codeDiplomeInfo?.rncp?.length === 1
            ? { code: codeDiplomeInfo.rncp[0].code, intitule: codeDiplomeInfo.rncp[0].intitule }
            : null
      }
    },
  })

  const codeRncpDisabled = computed<boolean>(() => {
    if (readonly.value) return true
    if (!model.value?.formation?.typeQualification) return true
    if (model.value.formation.typeQualification === 'Rncp') return false
    if (model.value.formation.codeDiplome?.intitule) return false
    return false
  })

  const codeDiplomeDisabled = computed<boolean>(() => {
    if (readonly.value) return true
    if (!model.value?.formation?.typeQualification) return true
    if (model.value.formation.typeQualification === 'CodeDiplome') return false
    if (model.value.formation.rncp?.intitule) return false
    return true
  })

  const typeQualificationModel = setupFieldWithOptions<TypeQualification | null>({
    getValue: () => model.value?.formation?.typeQualification ?? null,
    setPatch: (patch, value) => {
      patch.formation!.typeQualification = value
      patch.formation!.rncp = null
      patch.formation!.codeDiplome = null
    },
  })

  const getLabel = function (value: LovItemDTO<string>) {
    return `${value.code} - ${value.intitule}`
  }

  const intitulé = computed(() => {
    if (model.value?.formation?.rncp?.code) {
      return model.value?.formation?.rncp?.intitule
    } else if (model.value?.formation?.codeDiplome?.code) {
      return model.value?.formation?.codeDiplome?.intitule
    }
    return null
  })
</script>

